import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Currency, BillingMode, PackageTier, AuditResult } from './types';
import { PACKAGES } from './data/packages';
import { BLOG_POSTS, BlogPost, findBlogPostByPathOrSlug, saveCustomBlogPostsToClient } from './data/blogPosts';
import { ServiceKey, getServiceDetailByKeyOrSlug, SERVICE_DETAILS_BY_KEY } from './data/serviceDetails';
import { safeFetchJson } from './utils/apiClient';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { CorePillars } from './components/CorePillars';
import { IndustriesWeServe } from './components/IndustriesWeServe';
import { AutomationPipeline } from './components/AutomationPipeline';
import { CaseStudies } from './components/CaseStudies';
import { TrustindexReviewsSection } from './components/TrustindexReviewsSection';
import { LatestPostsHomeSection } from './components/LatestPostsHomeSection';
import { Footer } from './components/Footer';
import { LiveAIAssistantWidget } from './components/LiveAIAssistantWidget';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { AnimatePresence, motion } from 'motion/react';

// Lazy-loaded Modals (Only downloaded when opened)
const BookingQuoteModal = lazy(() =>
  import('./components/BookingQuoteModal').then((m) => ({ default: m.BookingQuoteModal }))
);
const AboutUsModal = lazy(() =>
  import('./components/AboutUsModal').then((m) => ({ default: m.AboutUsModal }))
);
const ContactModal = lazy(() =>
  import('./components/ContactModal').then((m) => ({ default: m.ContactModal }))
);
const AdminLoginModal = lazy(() =>
  import('./components/AdminLoginModal').then((m) => ({ default: m.AdminLoginModal }))
);

// Lazy-loaded Independent Pages (Code-split per route to reduce initial JS payload)
const ServicesPage = lazy(() =>
  import('./pages/ServicesPage').then((m) => ({ default: m.ServicesPage }))
);
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage'));
const PackagesPage = lazy(() =>
  import('./pages/PackagesPage').then((m) => ({ default: m.PackagesPage }))
);
const FaqPage = lazy(() =>
  import('./pages/FaqPage').then((m) => ({ default: m.FaqPage }))
);
const BlogPage = lazy(() =>
  import('./pages/BlogPage').then((m) => ({ default: m.BlogPage }))
);
const BlogPostPage = lazy(() =>
  import('./pages/BlogPostPage').then((m) => ({ default: m.BlogPostPage }))
);
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then((m) => ({ default: m.ContactPage }))
);
const XwebauditPage = lazy(() =>
  import('./pages/XwebauditPage').then((m) => ({ default: m.XwebauditPage }))
);
const SitemapPage = lazy(() =>
  import('./pages/SitemapPage').then((m) => ({ default: m.SitemapPage }))
);

// Lazy-loaded Admin Portal & Backend CRM
const AdminCrmPage = lazy(() =>
  import('./pages/AdminCrmPage').then((m) => ({ default: m.AdminCrmPage }))
);
const AdminLoginPage = lazy(() =>
  import('./pages/AdminLoginPage').then((m) => ({ default: m.AdminLoginPage }))
);

type ViewMode = 
  | 'home' 
  | 'services' 
  | 'service-detail'
  | 'packages' 
  | 'faq' 
  | 'blog' 
  | 'blog-post' 
  | 'contact' 
  | 'ai-websites' 
  | 'ai-search-visibility' 
  | 'ai-business-automation' 
  | 'xwebaudit' 
  | 'sitemap' 
  | 'crm' 
  | 'crm-login';

const PageLoadingFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-slate-50">
    <div className="w-8 h-8 rounded-full border-3 border-[#F26522] border-t-transparent animate-spin" aria-label="Loading page" />
  </div>
);

export default function App() {
  const [currency, setCurrency] = useState<Currency>('USD');
  const [activeServiceKey, setActiveServiceKey] = useState<ServiceKey>(() => {
    try {
      const matchedSvc =
        getServiceDetailByKeyOrSlug(window.location.pathname) ||
        getServiceDetailByKeyOrSlug(window.location.hash);
      if (matchedSvc) return matchedSvc.key;
      const savedKey = sessionStorage.getItem('xweba_active_service_key') as ServiceKey | null;
      if (savedKey && SERVICE_DETAILS_BY_KEY[savedKey]) return savedKey;
    } catch {}
    return 'branding';
  });
  const [activeBlogPost, setActiveBlogPost] = useState<BlogPost | null>(() => {
    try {
      const matchedFromUrl =
        findBlogPostByPathOrSlug(window.location.pathname) ||
        findBlogPostByPathOrSlug(window.location.hash);
      if (matchedFromUrl) return matchedFromUrl;
      const savedSlug = sessionStorage.getItem('xweba_active_blog_slug');
      if (savedSlug) {
        return findBlogPostByPathOrSlug(savedSlug) || null;
      }
    } catch {}
    return null;
  });
  const [currentView, setCurrentView] = useState<ViewMode>(() => {
    try {
      const matchedFromUrl =
        findBlogPostByPathOrSlug(window.location.pathname) ||
        findBlogPostByPathOrSlug(window.location.hash);
      if (matchedFromUrl) return 'blog-post';
      const matchedSvc =
        getServiceDetailByKeyOrSlug(window.location.pathname) ||
        getServiceDetailByKeyOrSlug(window.location.hash);
      if (matchedSvc) return 'service-detail';
      const saved = sessionStorage.getItem('xweba_active_view') as ViewMode | null;
      if (saved && ['home', 'services', 'service-detail', 'packages', 'faq', 'blog', 'blog-post', 'contact', 'xwebaudit', 'sitemap', 'ai-websites', 'ai-search-visibility', 'ai-business-automation'].includes(saved)) {
        return saved;
      }
    } catch {}
    return 'home';
  });
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isAdminLoginModalOpen, setIsAdminLoginModalOpen] = useState(false);
  const [modalPackage, setModalPackage] = useState<PackageTier | null>(null);
  const [modalBillingMode, setModalBillingMode] = useState<BillingMode>('waas');
  const [modalAuditContext, setModalAuditContext] = useState<AuditResult | null>(null);

  // Admin Auth & Session State
  const [adminToken, setAdminToken] = useState<string | null>(() => {
    return localStorage.getItem('xweba_admin_token') || sessionStorage.getItem('xweba_admin_token');
  });
  const [adminUser, setAdminUser] = useState<{ email: string; name: string; role: string } | null>(() => {
    const raw = localStorage.getItem('xweba_admin_user') || sessionStorage.getItem('xweba_admin_user');
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // Dedicated Xwebaudit prefill & execution state
  const [auditTargetDomain, setAuditTargetDomain] = useState<string>('');
  const [auditAutoRun, setAuditAutoRun] = useState<boolean>(false);

  // Sync with browser URL / hash
  useEffect(() => {
    const handleUrlChange = () => {
      const path = window.location.pathname.toLowerCase().replace(/\/+$/, '');
      const hash = window.location.hash.toLowerCase().replace(/\/+$/, '');
      const storedToken = localStorage.getItem('xweba_admin_token') || sessionStorage.getItem('xweba_admin_token');

      let resolvedView: ViewMode | null = null;
      const matchedPost = findBlogPostByPathOrSlug(path) || findBlogPostByPathOrSlug(hash);
      const matchedService = getServiceDetailByKeyOrSlug(path) || getServiceDetailByKeyOrSlug(hash);

      if (path === '/crm/login' || hash === '#crm/login' || hash === '#/crm/login') {
        if (storedToken) {
          resolvedView = 'crm';
          try { window.history.replaceState(null, '', '/crm'); } catch {}
        } else {
          resolvedView = 'crm-login';
        }
      } else if (path === '/crm' || hash === '#crm' || hash === '#/crm' || path.startsWith('/crm/')) {
        if (storedToken) {
          resolvedView = 'crm';
        } else {
          resolvedView = 'crm-login';
          try { window.history.replaceState(null, '', '/crm/login'); } catch {}
        }
      } else if (matchedPost) {
        setActiveBlogPost(matchedPost);
        resolvedView = 'blog-post';
        try {
          sessionStorage.setItem('xweba_active_blog_slug', matchedPost.slug);
        } catch {}
      } else if (matchedService) {
        setActiveServiceKey(matchedService.key);
        resolvedView = 'service-detail';
        try {
          sessionStorage.setItem('xweba_active_service_key', matchedService.key);
        } catch {}
      } else if (path === '/services' || hash === '#services' || hash === '#/services') {
        resolvedView = 'services';
      } else if (path === '/packages' || hash === '#packages' || hash === '#/packages') {
        resolvedView = 'packages';
      } else if (path === '/faq' || hash === '#faq' || hash === '#/faq') {
        resolvedView = 'faq';
      } else if (path === '/blog' || path.startsWith('/blog/') || hash === '#blog' || hash === '#/blog' || path === '/digital-pulse' || path.startsWith('/digital-pulse/') || hash === '#digital-pulse' || hash === '#/digital-pulse') {
        resolvedView = 'blog';
      } else if (path === '/contact' || hash === '#contact' || hash === '#/contact') {
        resolvedView = 'contact';
      } else if (path === '/sitemap' || hash === '#sitemap' || hash === '#/sitemap') {
        resolvedView = 'sitemap';
      } else if (path.includes('xwebaudit') || hash === '#xwebaudit' || path.includes('audit') || hash === '#audit') {
        resolvedView = 'xwebaudit';
      } else {
        try {
          const saved = sessionStorage.getItem('xweba_active_view') as ViewMode | null;
          if (saved && ['home', 'services', 'service-detail', 'packages', 'faq', 'blog', 'blog-post', 'contact', 'xwebaudit', 'sitemap', 'ai-websites', 'ai-search-visibility', 'ai-business-automation'].includes(saved)) {
            resolvedView = saved;
          }
        } catch {}
        if (!resolvedView) {
          resolvedView = 'home';
        }
      }

      setCurrentView(resolvedView);
      try {
        sessionStorage.setItem('xweba_active_view', resolvedView);
      } catch {}
    };

    const handleRedirectHomeEvent = () => {
      setIsBookingModalOpen(false);
      setIsContactModalOpen(false);
      setIsAboutModalOpen(false);
      setModalAuditContext(null);
      setAuditTargetDomain('');
      setAuditAutoRun(false);
      setCurrentView('home');
      try {
        sessionStorage.setItem('xweba_active_view', 'home');
        window.history.pushState(null, '', '/');
      } catch {
        window.location.hash = '/';
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    handleUrlChange();

    safeFetchJson<{ success: boolean; customPosts?: BlogPost[] }>('/api/blog/posts')
      .then((res) => {
        if (res.ok && Array.isArray(res.data?.customPosts) && res.data!.customPosts.length > 0) {
          saveCustomBlogPostsToClient(res.data!.customPosts);
          handleUrlChange();
        }
      })
      .catch(() => {});

    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('xweba:redirect-home', handleRedirectHomeEvent);
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('xweba:redirect-home', handleRedirectHomeEvent);
    };
  }, []);

  // Track Meta Pixel PageView on SPA Route Changes
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      try {
        (window as any).fbq('track', 'PageView');
      } catch (err) {
        console.debug('Meta Pixel tracking error:', err);
      }
    }
  }, [currentView, activeBlogPost?.slug, activeServiceKey]);

  const navigateTo = (view: ViewMode) => {
    setCurrentView(view);
    try {
      sessionStorage.setItem('xweba_active_view', view);
    } catch {}
    let targetPath = '/';
    if (view === 'crm') {
      targetPath = '/crm';
    } else if (view === 'crm-login') {
      targetPath = '/crm/login';
    } else if (view === 'services') {
      targetPath = '/services';
    } else if (view === 'service-detail') {
      targetPath = SERVICE_DETAILS_BY_KEY[activeServiceKey]?.path || '/services/branding-consultant';
    } else if (view === 'packages') {
      targetPath = '/packages';
    } else if (view === 'faq') {
      targetPath = '/faq';
    } else if (view === 'blog') {
      targetPath = '/blog';
    } else if (view === 'blog-post') {
      targetPath = activeBlogPost?.path || BLOG_POSTS[0].path;
    } else if (view === 'contact') {
      targetPath = '/contact';
    } else if (view === 'sitemap') {
      targetPath = '/sitemap';
    } else if (view === 'xwebaudit') {
      targetPath = '/xwebaudit';
    } else if (view === 'ai-websites') {
      setActiveServiceKey('websites');
      targetPath = '/services/pro-wordpress-ai-websites';
    } else if (view === 'ai-search-visibility') {
      setActiveServiceKey('search');
      targetPath = '/services/geo-optimization';
    } else if (view === 'ai-business-automation') {
      setActiveServiceKey('automation');
      targetPath = '/services/automation-pipeline';
    } else {
      targetPath = '/';
    }

    try {
      window.history.pushState(null, '', targetPath);
    } catch {
      window.location.hash = targetPath;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectBlogPost = (post: BlogPost) => {
    setActiveBlogPost(post);
    setCurrentView('blog-post');
    try {
      sessionStorage.setItem('xweba_active_view', 'blog-post');
      sessionStorage.setItem('xweba_active_blog_slug', post.slug);
      window.history.pushState(null, '', post.path);
    } catch {
      window.location.hash = post.path;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenAdminPortal = () => {
    if (adminToken) {
      navigateTo('crm');
    } else {
      navigateTo('crm-login');
    }
  };

  const handleLoginSuccess = (user: { email: string; name: string; role: string }, token: string) => {
    setAdminUser(user);
    setAdminToken(token);
    setIsAdminLoginModalOpen(false);
    navigateTo('crm');
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('xweba_admin_token');
    localStorage.removeItem('xweba_admin_user');
    sessionStorage.removeItem('xweba_admin_token');
    sessionStorage.removeItem('xweba_admin_user');
    setAdminToken(null);
    setAdminUser(null);
    navigateTo('crm-login');
  };

  const handleNavigateToService = (serviceKey: ServiceKey) => {
    const detail = SERVICE_DETAILS_BY_KEY[serviceKey] || SERVICE_DETAILS_BY_KEY.branding;
    setActiveServiceKey(detail.key);
    setCurrentView('service-detail');
    try {
      sessionStorage.setItem('xweba_active_view', 'service-detail');
      sessionStorage.setItem('xweba_active_service_key', detail.key);
      window.history.pushState(null, '', detail.path);
    } catch {
      window.location.hash = detail.path;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenBooking = (pkg?: PackageTier | null, mode: BillingMode = 'waas') => {
    setModalPackage(pkg || PACKAGES[1]); // Default to AI Growth
    setModalBillingMode(mode);
    setModalAuditContext(null);
    setIsBookingModalOpen(true);
  };

  const handleOpenAudit = (domain?: string) => {
    if (domain) {
      setAuditTargetDomain(domain);
      setAuditAutoRun(true);
    } else {
      setAuditTargetDomain('');
      setAuditAutoRun(false);
    }
    navigateTo('xwebaudit');
  };

  const handleSelectPackageForUpgrade = (packageName: string, auditContext?: AuditResult) => {
    const found = PACKAGES.find((p) => p.name.toLowerCase().includes(packageName.toLowerCase())) || PACKAGES[1];
    setModalPackage(found);
    setModalBillingMode('waas');
    if (auditContext) {
      setModalAuditContext(auditContext);
    }
    setIsBookingModalOpen(true);
  };

  if (currentView === 'crm') {
    return (
      <Suspense fallback={<PageLoadingFallback />}>
        <AdminCrmPage
          adminUser={adminUser || { email: 'info@xweba.com', name: 'Super Admin', role: 'Super Admin' }}
          authToken={adminToken || ''}
          onLogout={handleAdminLogout}
          onBackToPublicSite={() => navigateTo('home')}
        />
      </Suspense>
    );
  }

  if (currentView === 'crm-login') {
    return (
      <Suspense fallback={<PageLoadingFallback />}>
        <AdminLoginPage
          onLoginSuccess={handleLoginSuccess}
          onBackToHome={() => navigateTo('home')}
        />
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900 selection:bg-[#F26522] selection:text-white">
      {/* Fixed Sticky Header */}
      <Navbar
        currency={currency}
        onCurrencyChange={setCurrency}
        onOpenBooking={() => handleOpenBooking(null, 'waas')}
        onOpenAudit={() => handleOpenAudit()}
        onHomeClick={() => navigateTo('home')}
        onOpenContact={() => navigateTo('contact')}
        onNavigateToServices={() => navigateTo('services')}
        onNavigateToPackages={() => navigateTo('packages')}
        onNavigateToFaq={() => navigateTo('faq')}
        onNavigateToBlog={() => navigateTo('blog')}
        onNavigateToContact={() => navigateTo('contact')}
        currentView={currentView}
      />

      {/* Main Content Sections: Dedicated Independent Page or Home */}
      <main className={`flex-1 ${currentView !== 'home' ? 'pt-[68px]' : ''}`}>
        <Suspense fallback={<PageLoadingFallback />}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={
                currentView === 'service-detail' ||
                currentView === 'ai-websites' ||
                currentView === 'ai-search-visibility' ||
                currentView === 'ai-business-automation'
                  ? `service-${activeServiceKey}`
                  : currentView === 'blog-post'
                  ? `blog-${activeBlogPost?.slug || 'post'}`
                  : currentView
              }
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              className="page-transition-wrapper min-h-full flex flex-col"
            >
              {currentView === 'services' ? (
                <ServicesPage
                  currency={currency}
                  onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
                  onOpenAudit={() => handleOpenAudit()}
              onBackToHome={() => navigateTo('home')}
              onNavigateToPackages={() => navigateTo('packages')}
              onNavigateToService={handleNavigateToService}
              onNavigateToPackage={(pkgName) => handleSelectPackageForUpgrade(pkgName)}
            />
          ) : currentView === 'packages' ? (
            <PackagesPage
              currency={currency}
              onCurrencyChange={setCurrency}
              onSelectPackage={(pkg, mode) => handleOpenBooking(pkg, mode)}
              onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
              onOpenAudit={() => handleOpenAudit()}
              onBackToHome={() => navigateTo('home')}
              onNavigateToService={handleNavigateToService}
            />
          ) : currentView === 'faq' ? (
            <FaqPage
              onBackToHome={() => navigateTo('home')}
              onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
              onOpenContact={() => navigateTo('contact')}
            />
          ) : currentView === 'blog' ? (
            <BlogPage
              onBackToHome={() => navigateTo('home')}
              onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
              onOpenAudit={() => handleOpenAudit()}
              onNavigateToPackages={() => navigateTo('packages')}
              onNavigateToServices={() => navigateTo('services')}
              onSelectPost={handleSelectBlogPost}
            />
          ) : currentView === 'blog-post' ? (
            <BlogPostPage
              post={activeBlogPost || BLOG_POSTS[0]}
              onBackToBlog={() => navigateTo('blog')}
              onBackToHome={() => navigateTo('home')}
              onSelectPost={handleSelectBlogPost}
              onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
              onOpenAudit={() => handleOpenAudit()}
              onNavigateToPackages={() => navigateTo('packages')}
              onNavigateToServices={() => navigateTo('services')}
            />
          ) : currentView === 'contact' ? (
            <ContactPage
              onBackToHome={() => navigateTo('home')}
              onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
              onOpenAudit={() => handleOpenAudit()}
            />
          ) : currentView === 'sitemap' ? (
            <SitemapPage
              onBackToHome={() => navigateTo('home')}
              onNavigateToView={(view) => navigateTo(view)}
              onNavigateToService={handleNavigateToService}
              onSelectBlogPost={handleSelectBlogPost}
            />
          ) : currentView === 'xwebaudit' ? (
            <XwebauditPage
              currency={currency}
              initialDomain={auditTargetDomain}
              autoRun={auditAutoRun}
              onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
              onBackToHome={() => navigateTo('home')}
              onSelectPackageForUpgrade={handleSelectPackageForUpgrade}
            />
          ) : currentView === 'service-detail' ||
            currentView === 'ai-websites' ||
            currentView === 'ai-search-visibility' ||
            currentView === 'ai-business-automation' ? (
            <ServiceDetailPage
              serviceKey={
                currentView === 'ai-websites'
                  ? 'websites'
                  : currentView === 'ai-search-visibility'
                  ? 'search'
                  : currentView === 'ai-business-automation'
                  ? 'automation'
                  : activeServiceKey
              }
              onBackToServices={() => navigateTo('services')}
              onSelectService={handleNavigateToService}
              onOpenModal={() => handleOpenBooking(PACKAGES[1], 'waas')}
              onNavigateToAudit={() => handleOpenAudit()}
            />
          ) : (
          /* Independent Homepage Layout */
          <>
            {/* 1. Hero Section */}
            <Hero
              onOpenAudit={() => handleOpenAudit()}
              onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
              onNavigateToPackages={() => navigateTo('packages')}
            />

            {/* 2. Independent Pages Directory Gateway */}
            <section className="py-20 bg-slate-50 border-b border-slate-200">
              <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto text-center space-y-3 mb-12">
                  <div className="inline-flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-slate-500">
                    <span className="text-[#F26522] font-black">EXPLORE XWEBA</span>
                    <span aria-hidden="true" className="text-slate-300">·</span>
                    <span>DEDICATED PLATFORM PAGES</span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0B2D52] tracking-tight">
                    Everything your business needs to scale in the AI era
                  </h2>
                  <p className="text-slate-600 text-sm sm:text-base">
                    Navigate directly to our dedicated pages for full-stack capabilities, transparent WaaS packages, live AI search diagnostics, answers to common questions, and direct engineering advisory.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  <a
                    href="/services"
                    onClick={(e) => {
                      if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
                        e.preventDefault();
                        navigateTo('services');
                      }
                    }}
                    className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#F26522] shadow-2xs hover:shadow-md transition-all flex flex-col justify-between"
                  >
                    <div>
                      <span className="text-[11px] font-mono font-bold text-[#F26522] uppercase">01 · Core Services</span>
                      <h3 className="text-lg font-extrabold text-[#0B2D52] mt-1 group-hover:text-[#F26522] transition-colors">
                        Services &amp; Consulting
                      </h3>
                      <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                        Branding Consultant (20+ years hands-on design), Pro WordPress Websites cited by AI, Social Media Consulting, Digital / Smart Advertisement, and GEO.
                      </p>
                    </div>
                    <div className="pt-4 mt-4 border-t border-slate-100 text-xs font-bold text-[#0B2D52] group-hover:text-[#F26522] flex items-center justify-between">
                      <span>Open Services Page</span>
                      <span>→</span>
                    </div>
                  </a>

                  <a
                    href="/packages"
                    onClick={(e) => {
                      if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
                        e.preventDefault();
                        navigateTo('packages');
                      }
                    }}
                    className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#F26522] shadow-2xs hover:shadow-md transition-all flex flex-col justify-between"
                  >
                    <div>
                      <span className="text-[11px] font-mono font-bold text-[#F26522] uppercase">02 · Pricing</span>
                      <h3 className="text-lg font-extrabold text-[#0B2D52] mt-1 group-hover:text-[#F26522] transition-colors">
                        Packages & WaaS
                      </h3>
                      <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                        Compare AI Starter, Growth, Pro, and Enterprise tiers in USD or KES with our zero-upfront Website-as-a-Service model.
                      </p>
                    </div>
                    <div className="pt-4 mt-4 border-t border-slate-100 text-xs font-bold text-[#0B2D52] group-hover:text-[#F26522] flex items-center justify-between">
                      <span>Open Packages Page</span>
                      <span>→</span>
                    </div>
                  </a>

                  <a
                    href="/xwebaudit"
                    onClick={(e) => {
                      if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
                        e.preventDefault();
                        handleOpenAudit();
                      }
                    }}
                    className="group bg-[#0B2D52] text-white rounded-2xl p-6 border border-[#0B2D52] hover:border-[#F26522] shadow-md hover:shadow-lg transition-all flex flex-col justify-between"
                  >
                    <div>
                      <span className="text-[11px] font-mono font-bold text-[#F26522] uppercase">03 · Diagnostic</span>
                      <h3 className="text-lg font-extrabold text-white mt-1 group-hover:text-[#F26522] transition-colors">
                        Free Xwebaudit
                      </h3>
                      <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                        Test any domain live for ChatGPT & Perplexity visibility, Schema.org entity graphs, and /llms.txt readiness.
                      </p>
                    </div>
                    <div className="pt-4 mt-4 border-t border-white/10 text-xs font-bold text-[#F26522] flex items-center justify-between">
                      <span>Launch Xwebaudit</span>
                      <span>→</span>
                    </div>
                  </a>

                  <a
                    href="/faq"
                    onClick={(e) => {
                      if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
                        e.preventDefault();
                        navigateTo('faq');
                      }
                    }}
                    className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#F26522] shadow-2xs hover:shadow-md transition-all flex flex-col justify-between"
                  >
                    <div>
                      <span className="text-[11px] font-mono font-bold text-[#F26522] uppercase">04 · Knowledge</span>
                      <h3 className="text-lg font-extrabold text-[#0B2D52] mt-1 group-hover:text-[#F26522] transition-colors">
                        FAQ
                      </h3>
                      <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                        Clear answers on Generative Engine Optimization (GEO), WaaS ownership, Hostinger Cloud, and NGO subsidies.
                      </p>
                    </div>
                    <div className="pt-4 mt-4 border-t border-slate-100 text-xs font-bold text-[#0B2D52] group-hover:text-[#F26522] flex items-center justify-between">
                      <span>Open FAQ Page</span>
                      <span>→</span>
                    </div>
                  </a>

                  <a
                    href="/blog"
                    onClick={(e) => {
                      if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
                        e.preventDefault();
                        navigateTo('blog');
                      }
                    }}
                    className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#F26522] shadow-2xs hover:shadow-md transition-all flex flex-col justify-between"
                  >
                    <div>
                      <span className="text-[11px] font-mono font-bold text-[#F26522] uppercase">05 · Digital Pulse</span>
                      <h3 className="text-lg font-extrabold text-[#0B2D52] mt-1 group-hover:text-[#F26522] transition-colors">
                        Digital Pulse
                      </h3>
                      <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                        In-depth playbooks on ChatGPT & Perplexity GEO, /llms.txt standards, WaaS economics, and 3-second CRM automation.
                      </p>
                    </div>
                    <div className="pt-4 mt-4 border-t border-slate-100 text-xs font-bold text-[#0B2D52] group-hover:text-[#F26522] flex items-center justify-between">
                      <span>Open Digital Pulse</span>
                      <span>→</span>
                    </div>
                  </a>

                  <a
                    href="/contact"
                    onClick={(e) => {
                      if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
                        e.preventDefault();
                        navigateTo('contact');
                      }
                    }}
                    className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#F26522] shadow-2xs hover:shadow-md transition-all flex flex-col justify-between"
                  >
                    <div>
                      <span className="text-[11px] font-mono font-bold text-[#F26522] uppercase">06 · Direct Desk</span>
                      <h3 className="text-lg font-extrabold text-[#0B2D52] mt-1 group-hover:text-[#F26522] transition-colors">
                        Contact
                      </h3>
                      <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                        Send a direct message to info@xweba.com, connect on WhatsApp (+254 725 724 175), or book a strategy call.
                      </p>
                    </div>
                    <div className="pt-4 mt-4 border-t border-slate-100 text-xs font-bold text-[#0B2D52] group-hover:text-[#F26522] flex items-center justify-between">
                      <span>Open Contact Page</span>
                      <span>→</span>
                    </div>
                  </a>
                </div>
              </div>
            </section>

            {/* 2B. Core Services & 20+ Years Branding Heritage */}
            <CorePillars
              onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
              onNavigateToService={handleNavigateToService}
            />

            {/* 3. Industries We Serve (Featuring Non-Profits & NGOs) */}
            <IndustriesWeServe
              onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
            />

            {/* 4. The 7-Step Visitor-to-Revenue Automation Pipeline */}
            <AutomationPipeline />

            {/* 5. Proven 2026 Case Studies */}
            <CaseStudies />

            {/* 6. Trustindex & Google Verified Client Reviews */}
            <TrustindexReviewsSection />

            {/* 7. Latest 4 Blog Post Cards */}
            <LatestPostsHomeSection
              onSelectPost={handleSelectBlogPost}
              onNavigateToBlog={() => navigateTo('blog')}
            />
          </>
        )}
            </motion.div>
          </AnimatePresence>
        </Suspense>
      </main>

      {/* Footer */}
      <Footer
        onOpenBooking={() => handleOpenBooking(null, 'waas')}
        onOpenAudit={handleOpenAudit}
        onOpenAbout={() => setIsAboutModalOpen(true)}
        onNavigateToService={handleNavigateToService}
        onNavigateToServices={() => navigateTo('services')}
        onNavigateToPackages={() => navigateTo('packages')}
        onNavigateToFaq={() => navigateTo('faq')}
        onNavigateToBlog={() => navigateTo('blog')}
        onNavigateToContact={() => navigateTo('contact')}
        onNavigateToSitemap={() => navigateTo('sitemap')}
        onOpenContact={() => navigateTo('contact')}
        onOpenAdminLogin={handleOpenAdminPortal}
        isAdminLoggedIn={!!adminToken}
      />

      {/* Interactive 24/7 AI Concierge Chatbot Widget */}
      <LiveAIAssistantWidget
        onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
        onOpenAudit={handleOpenAudit}
        onNavigateToPackages={() => navigateTo('packages')}
        onNavigateToServices={() => navigateTo('services')}
        onNavigateToContact={() => navigateTo('contact')}
        onNavigateToFaq={() => navigateTo('faq')}
        currency={currency}
        currentView={currentView}
      />

      {/* GDPR Slide-Up Cookie Consent Banner & Privacy Policy Modal */}
      <CookieConsentBanner />

      {/* Lazy-Loaded Modals (Only fetched & mounted when opened) */}
      <Suspense fallback={null}>
        {isBookingModalOpen && (
          <BookingQuoteModal
            isOpen={isBookingModalOpen}
            onClose={() => setIsBookingModalOpen(false)}
            currency={currency}
            initialPackage={modalPackage}
            initialBillingMode={modalBillingMode}
            initialService={
              currentView === 'service-detail' ||
              currentView === 'ai-websites' ||
              currentView === 'ai-search-visibility' ||
              currentView === 'ai-business-automation'
                ? activeServiceKey === 'branding'
                  ? 'Branding Consultant (20+ Yrs)'
                  : activeServiceKey === 'social'
                  ? 'Social Media Consulting'
                  : activeServiceKey === 'ads'
                  ? 'Digital / Smart Ads'
                  : activeServiceKey === 'search' || currentView === 'ai-search-visibility'
                  ? 'AI Search Visibility (GEO)'
                  : activeServiceKey === 'automation' || currentView === 'ai-business-automation'
                  ? 'AI CRM & Automation'
                  : activeServiceKey === 'hosting'
                  ? 'Managed Cloud Hosting'
                  : 'Pro WordPress & AI Websites'
                : undefined
            }
            auditContext={modalAuditContext}
          />
        )}

        {isAboutModalOpen && (
          <AboutUsModal
            isOpen={isAboutModalOpen}
            onClose={() => setIsAboutModalOpen(false)}
            onOpenBooking={() => handleOpenBooking(PACKAGES[1], 'waas')}
          />
        )}

        {isContactModalOpen && (
          <ContactModal
            isOpen={isContactModalOpen}
            onClose={() => setIsContactModalOpen(false)}
          />
        )}

        {isAdminLoginModalOpen && (
          <AdminLoginModal
            isOpen={isAdminLoginModalOpen}
            onClose={() => setIsAdminLoginModalOpen(false)}
            onLoginSuccess={handleLoginSuccess}
          />
        )}
      </Suspense>
    </div>
  );
}
